/**
 * JFX Wunschliste (Gast-Wishlist)
 * ---------------------------------------------------------------------------
 * Speichert ausschliesslich stabile Produkt-Identifikatoren (ID + Handle) im
 * localStorage des Browsers. Keine personenbezogenen Daten, keine Preise,
 * keine Produktobjekte. Titel/Preis/Bild/Verfuegbarkeit werden beim Aufruf der
 * Wunschliste serverseitig von Shopify gerendert.
 *
 * Oeffentliche API:  window.JFXWishlist
 *   .ready              {Boolean}
 *   .storageAvailable   {Boolean}
 *   .getItems()         -> [{id, handle, addedAt}]
 *   .getIds()           -> [Number]
 *   .getHandles()       -> [String]
 *   .has(id)            -> Boolean
 *   .add(id, handle)    -> Boolean
 *   .remove(id)         -> Boolean
 *   .toggle(id, handle) -> Boolean (neuer Zustand)
 *   .removeByHandle(h)  -> Boolean
 *   .prune(handles)     -> Number (entfernte Eintraege)
 *   .count()            -> Number
 *   .clear()            -> void
 *   .syncButtons(root)  -> void
 *   .announce(text)     -> void
 *
 * Event: document -> 'wishlist:updated'
 *   detail = { count, ids, handles, action, productId, handle, source }
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // Mehrfaches Initialisieren derselben Komponente verhindern.
  if (window.JFXWishlist && window.JFXWishlist.ready) return;

  var STORAGE_KEY = 'jfx_wishlist_v1';
  var LEGACY_KEYS = ['jfx_wishlist', 'wishlist'];
  var SCHEMA_VERSION = 1;
  var MAX_ITEMS = 100;
  var EVENT_NAME = 'wishlist:updated';

  var CFG = window.JFXWishlistConfig || {};

  var TEXT = Object.assign({
    add: 'Zur Wunschliste hinzufügen',
    remove: 'Von der Wunschliste entfernen',
    addedLive: 'Produkt zur Wunschliste hinzugefügt.',
    removedLive: 'Produkt von der Wunschliste entfernt.',
    fullLive: 'Die Wunschliste ist voll. Es können maximal 100 Produkte gespeichert werden.',
    noStorageLive: 'Die Wunschliste kann in diesem Browser nicht dauerhaft gespeichert werden.'
  }, CFG.text || {});

  /* ---------------------------------------------------------------------- */
  /* Storage-Adapter: localStorage mit Fallback in den Arbeitsspeicher        */
  /* ---------------------------------------------------------------------- */

  var Storage = (function () {
    var available = false;
    var memory = null;

    try {
      var probe = '__jfx_wl_probe__';
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      available = true;
    } catch (e) {
      available = false;
    }

    return {
      available: available,
      read: function () {
        if (!available) return memory;
        try {
          return window.localStorage.getItem(STORAGE_KEY);
        } catch (e) {
          return null;
        }
      },
      write: function (value) {
        if (!available) {
          memory = value;
          return true;
        }
        try {
          window.localStorage.setItem(STORAGE_KEY, value);
          return true;
        } catch (e) {
          // z.B. QuotaExceededError im privaten Modus.
          memory = value;
          return false;
        }
      },
      readLegacy: function (key) {
        if (!available) return null;
        try {
          return window.localStorage.getItem(key);
        } catch (e) {
          return null;
        }
      },
      removeLegacy: function (key) {
        if (!available) return;
        try {
          window.localStorage.removeItem(key);
        } catch (e) { /* ignorieren */ }
      }
    };
  }());

  /* ---------------------------------------------------------------------- */
  /* Validierung und Migration                                               */
  /* ---------------------------------------------------------------------- */

  var HANDLE_RE = /^[a-z0-9](?:[a-z0-9-_]{0,253}[a-z0-9])?$/i;

  function normaliseItem(raw) {
    if (!raw) return null;

    var id;
    var handle;
    var addedAt;

    if (typeof raw === 'number' || typeof raw === 'string') {
      // Legacy: reine ID oder reiner Handle.
      if (/^\d+$/.test(String(raw))) {
        id = parseInt(raw, 10);
      } else if (HANDLE_RE.test(String(raw))) {
        handle = String(raw);
      } else {
        return null;
      }
    } else if (typeof raw === 'object') {
      id = raw.id != null ? parseInt(raw.id, 10) : NaN;
      handle = typeof raw.handle === 'string' ? raw.handle : (typeof raw.h === 'string' ? raw.h : undefined);
      addedAt = parseInt(raw.addedAt != null ? raw.addedAt : raw.t, 10);
    } else {
      return null;
    }

    if (id != null && (!isFinite(id) || id <= 0)) id = undefined;
    if (handle && !HANDLE_RE.test(handle)) handle = undefined;
    if (id == null && !handle) return null;
    if (!isFinite(addedAt) || addedAt <= 0) addedAt = Date.now();

    return { id: id || null, handle: handle || null, addedAt: addedAt };
  }

  function dedupe(items) {
    var seen = Object.create(null);
    var out = [];
    for (var i = 0; i < items.length; i += 1) {
      var key = items[i].id ? 'i' + items[i].id : 'h' + items[i].handle;
      if (seen[key]) continue;
      seen[key] = true;
      out.push(items[i]);
    }
    return out;
  }

  function parse(rawString) {
    if (!rawString) return [];
    var data;
    try {
      data = JSON.parse(rawString);
    } catch (e) {
      return [];
    }

    var list;
    if (Array.isArray(data)) {
      list = data;                               // Legacy-Format
    } else if (data && Array.isArray(data.items)) {
      list = data.items;                         // Aktuelles Format
    } else {
      return [];
    }

    var items = [];
    for (var i = 0; i < list.length; i += 1) {
      var item = normaliseItem(list[i]);
      if (item) items.push(item);
    }
    return dedupe(items).slice(0, MAX_ITEMS);
  }

  function serialise(items) {
    return JSON.stringify({
      v: SCHEMA_VERSION,
      updatedAt: Date.now(),
      items: items.map(function (item) {
        return { id: item.id, handle: item.handle, addedAt: item.addedAt };
      })
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Zustand                                                                 */
  /* ---------------------------------------------------------------------- */

  var state = [];

  function load() {
    var raw = Storage.read();
    var items = parse(raw);

    if (!raw) {
      // Einmalige Migration aus alten Schluesseln.
      for (var i = 0; i < LEGACY_KEYS.length; i += 1) {
        var legacy = Storage.readLegacy(LEGACY_KEYS[i]);
        if (legacy) {
          var migrated = parse(legacy);
          if (migrated.length) {
            items = dedupe(items.concat(migrated)).slice(0, MAX_ITEMS);
          }
          Storage.removeLegacy(LEGACY_KEYS[i]);
        }
      }
      if (items.length) Storage.write(serialise(items));
    } else {
      // Ungueltige/veraltete Eintraege wurden entfernt -> bereinigt zurueckschreiben.
      var rawCount = 0;
      try {
        var parsed = JSON.parse(raw);
        rawCount = Array.isArray(parsed)
          ? parsed.length
          : (parsed && Array.isArray(parsed.items) ? parsed.items.length : -1);
      } catch (e) {
        rawCount = -1;
      }
      if (rawCount !== items.length) Storage.write(serialise(items));
    }

    state = items;
    return state;
  }

  function persist() {
    Storage.write(serialise(state));
  }

  function indexOfId(id) {
    var numeric = parseInt(id, 10);
    for (var i = 0; i < state.length; i += 1) {
      if (state[i].id === numeric) return i;
    }
    return -1;
  }

  function indexOfHandle(handle) {
    for (var i = 0; i < state.length; i += 1) {
      if (state[i].handle === handle) return i;
    }
    return -1;
  }

  /* ---------------------------------------------------------------------- */
  /* Screenreader-Ausgabe                                                    */
  /* ---------------------------------------------------------------------- */

  var liveRegion = null;
  var liveTimer = null;

  function getLiveRegion() {
    if (liveRegion && document.body.contains(liveRegion)) return liveRegion;
    liveRegion = document.getElementById('jfx-wishlist-live');
    if (!liveRegion) {
      liveRegion = document.createElement('p');
      liveRegion.id = 'jfx-wishlist-live';
      liveRegion.className = 'jfx-wl-visually-hidden';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion);
    }
    return liveRegion;
  }

  function announce(message) {
    if (!message) return;
    try {
      var region = getLiveRegion();
      window.clearTimeout(liveTimer);
      region.textContent = '';
      liveTimer = window.setTimeout(function () {
        region.textContent = message;
      }, 60);
    } catch (e) { /* Ausgabe ist optional */ }
  }

  /* ---------------------------------------------------------------------- */
  /* Tracking (nur wenn im Theme bereits ein dataLayer-Schema existiert)      */
  /* ---------------------------------------------------------------------- */

  function track(eventName, detail) {
    if (!CFG.tracking) return;                       // Standard: aus
    if (!Array.isArray(window.dataLayer)) return;
    try {
      window.dataLayer.push({ event: eventName, jfx_wishlist: detail });
    } catch (e) { /* Tracking darf die Funktion nie blockieren */ }
  }

  /* ---------------------------------------------------------------------- */
  /* Benachrichtigung                                                        */
  /* ---------------------------------------------------------------------- */

  function emit(action, item, source) {
    var detail = {
      count: state.length,
      ids: api.getIds(),
      handles: api.getHandles(),
      action: action,
      productId: item ? item.id : null,
      handle: item ? item.handle : null,
      source: source || 'script'
    };

    try {
      document.dispatchEvent(new CustomEvent(EVENT_NAME, { bubbles: true, detail: detail }));
    } catch (e) {
      var evt = document.createEvent('CustomEvent');
      evt.initCustomEvent(EVENT_NAME, true, false, detail);
      document.dispatchEvent(evt);
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Oeffentliche API                                                        */
  /* ---------------------------------------------------------------------- */

  var api = {
    ready: false,
    storageAvailable: Storage.available,
    maxItems: MAX_ITEMS,
    eventName: EVENT_NAME,
    text: TEXT,

    getItems: function () {
      return state.slice();
    },

    getIds: function () {
      return state.map(function (item) { return item.id; }).filter(Boolean);
    },

    getHandles: function () {
      return state.map(function (item) { return item.handle; }).filter(Boolean);
    },

    has: function (id) {
      return indexOfId(id) > -1;
    },

    count: function () {
      return state.length;
    },

    isFull: function () {
      return state.length >= MAX_ITEMS;
    },

    add: function (id, handle, source) {
      var item = normaliseItem({ id: id, handle: handle, addedAt: Date.now() });
      if (!item) return false;
      if (item.id != null && indexOfId(item.id) > -1) return false;   // keine Doppelspeicherung
      if (item.id == null && indexOfHandle(item.handle) > -1) return false;

      if (state.length >= MAX_ITEMS) {
        announce(TEXT.fullLive);
        return false;
      }

      state.unshift(item);
      persist();
      if (!Storage.available) announce(TEXT.noStorageLive);
      announce(TEXT.addedLive);
      syncButtons();
      emit('add', item, source);
      track('jfx_wishlist_add', { product_id: item.id, handle: item.handle, count: state.length });
      return true;
    },

    remove: function (id, source) {
      var index = indexOfId(id);
      if (index < 0) return false;
      var item = state[index];
      state.splice(index, 1);
      persist();
      announce(TEXT.removedLive);
      syncButtons();
      emit('remove', item, source);
      track('jfx_wishlist_remove', { product_id: item.id, handle: item.handle, count: state.length });
      return true;
    },

    removeByHandle: function (handle, source) {
      var index = indexOfHandle(handle);
      if (index < 0) return false;
      return api.remove(state[index].id, source);
    },

    /** Entfernt Eintraege, die es im Shop nicht mehr gibt. */
    prune: function (handles) {
      if (!handles || !handles.length) return 0;
      var before = state.length;
      state = state.filter(function (item) {
        return handles.indexOf(item.handle) === -1;
      });
      if (state.length !== before) {
        persist();
        syncButtons();
        emit('prune', null, 'prune');
      }
      return before - state.length;
    },

    toggle: function (id, handle, source) {
      if (api.has(id)) {
        api.remove(id, source);
        return false;
      }
      return api.add(id, handle, source);
    },

    clear: function (source) {
      if (!state.length) return;
      state = [];
      persist();
      syncButtons();
      emit('clear', null, source);
    },

    reload: function () {
      load();
      syncButtons();
      emit('sync', null, 'reload');
    },

    announce: announce,
    syncButtons: function (root) { syncButtons(root); }
  };

  /* ---------------------------------------------------------------------- */
  /* Buttons: Zustand synchronisieren                                        */
  /* ---------------------------------------------------------------------- */

  function syncButton(button) {
    var id = button.getAttribute('data-product-id');
    var active = id
      ? indexOfId(id) > -1
      : indexOfHandle(button.getAttribute('data-product-handle')) > -1;

    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.setAttribute('aria-label', active ? TEXT.remove : TEXT.add);
    button.setAttribute('title', active ? TEXT.remove : TEXT.add);
    button.classList.toggle('is-active', active);

    var label = button.querySelector('[data-jfx-wl-label]');
    if (label) label.textContent = active ? TEXT.remove : TEXT.add;
  }

  function syncButtons(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var buttons = scope.querySelectorAll('[data-jfx-wl-toggle]');
    for (var i = 0; i < buttons.length; i += 1) syncButton(buttons[i]);
    syncCounters();
  }

  function syncCounters() {
    var count = state.length;
    var nodes = document.querySelectorAll('[data-jfx-wl-count]');
    for (var i = 0; i < nodes.length; i += 1) {
      var node = nodes[i];
      node.textContent = count > 99 ? '99+' : String(count);
      node.setAttribute('data-count', String(count));
      var wrapper = node.closest ? node.closest('[data-jfx-wl-counter]') : null;
      if (wrapper) {
        wrapper.classList.toggle('is-empty', count === 0);
        var a11y = wrapper.querySelector('[data-jfx-wl-count-a11y]');
        if (a11y) {
          a11y.textContent = count === 1
            ? 'Wunschliste, 1 Produkt'
            : 'Wunschliste, ' + count + ' Produkte';
        }
      }
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Event-Delegation (funktioniert auch fuer dynamisch nachgeladene Karten)  */
  /* ---------------------------------------------------------------------- */

  function onDocumentClick(evt) {
    var button = evt.target && evt.target.closest ? evt.target.closest('[data-jfx-wl-toggle]') : null;
    if (!button) return;

    // Kein Navigieren zur Produktseite, kein Ausloesen von Karten-Links.
    evt.preventDefault();
    evt.stopPropagation();

    var id = button.getAttribute('data-product-id');
    var handle = button.getAttribute('data-product-handle');
    var wasActive = api.has(id);

    api.toggle(id, handle, button.getAttribute('data-jfx-wl-source') || 'button');

    // Sichtbares Feedback nur bei echtem Hinzufuegen.
    if (!wasActive && api.has(id)) {
      button.classList.add('jfx-wl-btn--pulse');
      window.setTimeout(function () {
        button.classList.remove('jfx-wl-btn--pulse');
      }, 420);
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Initialisierung                                                         */
  /* ---------------------------------------------------------------------- */

  var observer = null;
  var syncFrame = null;

  function scheduleSync() {
    if (syncFrame) return;
    syncFrame = window.requestAnimationFrame(function () {
      syncFrame = null;
      syncButtons();
    });
  }

  function init() {
    load();

    document.addEventListener('click', onDocumentClick, true);

    // Mehrere Tabs desselben Browsers synchron halten.
    window.addEventListener('storage', function (evt) {
      if (evt.key && evt.key !== STORAGE_KEY) return;
      load();
      syncButtons();
      emit('sync', null, 'storage-event');
    });

    // Dynamisch nachgeladene Sections (Filter, Paginierung, Theme-Editor).
    document.addEventListener('shopify:section:load', function (evt) {
      syncButtons(evt.target || document);
    });
    document.addEventListener('shopify:block:select', scheduleSync);

    if ('MutationObserver' in window) {
      observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i += 1) {
          if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
            scheduleSync();
            return;
          }
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    syncButtons();
    api.ready = true;
    emit('init', null, 'init');
  }

  window.JFXWishlist = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}());
